/**
 * <s-product-gallery> — PDP gallery with a main image, swappable thumbnails and in-place zoom.
 *
 * Desktop (>= 768px): the [data-gallery-main] slot is large; other slots are thumbnails.
 *   Clicking a thumbnail's [data-gallery-select] button swaps its media into the main slot.
 * Zoom: images never open in a dialog. Mouse/pen hover magnifies the main image under the pointer;
 *   on touch, press and hold an image to magnify it and drag to pan; releasing resets it.
 *
 * Required markup:
 *   [data-gallery-slot]            — each slot; the first also carries [data-gallery-main], others [data-thumb].
 *   [data-gallery-item]            — the movable media inside a slot.
 *   [data-zoom-frame] img          — zoomable image frames.
 *   [data-gallery-select]          — thumbnail button.
 */
const ZOOM_SCALE = 2.2;
const HOLD_MS = 350;
const MOVE_CANCEL_PX = 8;

class SProductGallery extends HTMLElement {
  #ac;
  #desktop = window.matchMedia('(min-width: 768px)');
  #holdTimer = 0;
  #touchFrame = null;
  #touchStart = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleSelect, opts);
    this.addEventListener('pointermove', this.#handlePointerMove, opts);
    this.addEventListener('pointerout', this.#handlePointerOut, opts);
    this.addEventListener('touchstart', this.#handleTouchStart, { ...opts, passive: true });
    this.addEventListener('touchmove', this.#handleTouchMove, { ...opts, passive: true });
    this.addEventListener('touchend', this.#endTouch, opts);
    this.addEventListener('touchcancel', this.#endTouch, opts);
    this.addEventListener('contextmenu', (event) => event.preventDefault(), opts);

    // Phones: keep the [data-gallery-dot] indicators in step with the swipe position.
    const track = this.querySelector('[data-product-gallery]');
    if (track && this.querySelector('[data-gallery-dot]')) {
      track.addEventListener('scroll', () => this.#syncDots(track), { ...opts, passive: true });
      this.#autoplay(track);
    }
  }

  // Phones: auto-advance one photo every 3.5s and loop back to the first. Pauses for 8s after any touch, while the
  // gallery is off-screen or the tab is hidden, and while a photo is zoomed. Never runs for reduced motion.
  #autoplay(track) {
    if (track.children.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const signal = this.#ac.signal;
    let holdUntil = Date.now() + 1500;
    let visible = false;
    const hold = () => { holdUntil = Date.now() + 8000; };
    this.addEventListener('touchstart', hold, { signal, passive: true });
    this.addEventListener('pointerdown', hold, { signal });
    const observer = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
    }, { threshold: 0.5 });
    observer.observe(track);
    const timer = setInterval(() => {
      if (this.#desktop.matches || !visible || document.hidden || Date.now() < holdUntil) return;
      if (this.#touchFrame || this.querySelector('[data-zoom-img][style*="scale"]')) return;
      const width = track.clientWidth;
      if (!width) return;
      const rtl = getComputedStyle(track).direction === 'rtl';
      const position = Math.abs(track.scrollLeft);
      const atEnd = position + width >= track.scrollWidth - 4;
      const next = atEnd ? 0 : Math.round(position / width + 1) * width;
      track.scrollTo({ left: rtl ? -next : next, behavior: 'smooth' });
    }, 3500);
    signal.addEventListener('abort', () => {
      clearInterval(timer);
      observer.disconnect();
    }, { once: true });
  }

  #syncDots(track) {
    const dots = this.querySelectorAll('[data-gallery-dot]');
    const width = track.clientWidth || 1;
    const index = Math.min(dots.length - 1, Math.round(Math.abs(track.scrollLeft) / width));
    dots.forEach((dot, n) => dot.toggleAttribute('data-active', n === index));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#holdTimer);
  }

  #handleSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-gallery-select]');
    if (!button || !this.contains(button)) return;

    const slot = button.closest('[data-gallery-slot]');
    const main = this.querySelector('[data-gallery-main]');
    if (!slot || !main || slot === main) return;

    const slotItem = slot.querySelector('[data-gallery-item]');
    const mainItem = main.querySelector('[data-gallery-item]');
    if (!slotItem || !mainItem) return;

    const slotLabel = button.getAttribute('aria-label');
    const mainButton = main.querySelector('[data-gallery-select]');
    const mainLabel = mainButton?.getAttribute('aria-label');

    slot.prepend(mainItem);
    main.prepend(slotItem);
    if (mainButton && slotLabel && mainLabel) {
      mainButton.setAttribute('aria-label', slotLabel);
      button.setAttribute('aria-label', mainLabel);
    }
    for (const img of this.querySelectorAll('[data-zoom-img]')) this.#reset(img);
  };

  #zoomableFrame(target) {
    if (!(target instanceof Element)) return null;
    const frame = target.closest('[data-zoom-frame]');
    if (!frame || !this.contains(frame)) return null;
    // On desktop only the main image zooms; thumbnails are for switching.
    if (this.#desktop.matches && !frame.closest('[data-gallery-main]')) return null;
    return frame;
  }

  #zoomAt(frame, clientX, clientY) {
    const img = frame.querySelector('[data-zoom-img]');
    if (!(img instanceof HTMLElement)) return;
    const rect = frame.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    img.style.transformOrigin = `${x}% ${y}%`;
    img.style.transform = `scale(${ZOOM_SCALE})`;
  }

  #reset(img) {
    if (!(img instanceof HTMLElement)) return;
    img.style.transform = '';
    img.style.transformOrigin = '';
  }

  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    const frame = this.#zoomableFrame(event.target);
    if (!frame) return;
    this.#zoomAt(frame, event.clientX, event.clientY);
  };

  #handlePointerOut = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;
    const frame = event.target.closest('[data-zoom-frame]');
    if (!frame || (event.relatedTarget instanceof Node && frame.contains(event.relatedTarget))) return;
    this.#reset(frame.querySelector('[data-zoom-img]'));
  };

  #handleTouchStart = (event) => {
    const frame = this.#zoomableFrame(event.target);
    if (!frame || event.touches.length !== 1) return;
    const touch = event.touches[0];
    this.#touchStart = { x: touch.clientX, y: touch.clientY };
    clearTimeout(this.#holdTimer);
    this.#holdTimer = setTimeout(() => {
      this.#touchFrame = frame;
      this.#zoomAt(frame, this.#touchStart.x, this.#touchStart.y);
      // Only while zoomed do we take over the finger, so normal scrolling stays smooth.
      document.addEventListener('touchmove', this.#handleZoomMove, { passive: false, signal: this.#ac.signal });
    }, HOLD_MS);
  };

  // Passive: a finger that moves before the hold registers is a normal scroll or swipe.
  #handleTouchMove = (event) => {
    if (this.#touchFrame || !this.#touchStart) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dx = Math.abs(touch.clientX - this.#touchStart.x);
    const dy = Math.abs(touch.clientY - this.#touchStart.y);
    if (dx > MOVE_CANCEL_PX || dy > MOVE_CANCEL_PX) clearTimeout(this.#holdTimer);
  };

  #handleZoomMove = (event) => {
    if (!this.#touchFrame) return;
    event.preventDefault();
    const touch = event.touches[0];
    if (touch) this.#zoomAt(this.#touchFrame, touch.clientX, touch.clientY);
  };

  #endTouch = () => {
    clearTimeout(this.#holdTimer);
    document.removeEventListener('touchmove', this.#handleZoomMove);
    if (this.#touchFrame) this.#reset(this.#touchFrame.querySelector('[data-zoom-img]'));
    this.#touchFrame = null;
    this.#touchStart = null;
  };
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
